import './TemplateDetailsBox.scss'
import classNames from 'classnames'
import { type CustomTemplateScope, type TemplateCloneRequest, type TemplateUpdateRequest, type TemplateValue } from '../../../models/TemplateLibrary'
import { TemplateUsedInModal } from './TemplateUsedInModal'
import React, { useMemo, useState } from 'react'
import { Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { TemplateTypeBadge } from './TemplateTypeBadge'
import { hasLexicalEmailFormat } from './EmailTemplateEditor/emailTemplateFormat'
import { schedulingEmailDetails } from './SchedulingEmails/schedulingEmails'
import { TemplateScopeBadge } from './TemplateScopeBadge'
import { useGetUserByIdQuery } from '../../../store/user/user'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { SystemUserBadge, TemplateCreatedByUserBadge } from './TemplateCreatedByUserBadge'
import FormattedDate from '../../../components/FormattedDate'
import { Button } from '../../../components/Button/Button'
import { TemplateDuplicateModal } from './TemplateDuplicateModal'
import { TemplateChangeAccessModal } from './TemplateChangeAccessModal'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import {
  useCloneTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplateReferencesQuery,
  useUpdateTemplateMutation,
} from '../../../store/templateLibrary/templateLibrary'
import { useNavigate } from 'react-router-dom'
import {
  inputParametersHierarchyApi,
  useCreateNodeMutation,
  useUpdateNodeMutation,
  type InputParametersHierarchyContext,
} from '../../../store/ai/inputParametersHierarchy'
import { calculateHierarchyId } from './utils'
import { type TemplateOrigin } from './templateOrigin'
import { TemplateScopeMapping } from './TemplateScopeMapping'
import { TemplateEditModal } from './TemplateEditModal'
import { ADMIN_EDITABLE_TEMPLATE_SCOPES, isAdminManagedTemplate, ZOCKS_SYSTEM_USER_ID } from './utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails } from './tracking'
import { useAccount } from '../../../common/useAccount'
import { SuccessToast } from '../../../components/SuccessToast/SuccessToast'
import { isSchedulingEmailTemplate } from './TemplatesTab'
import { TemplateDeleteConfirmationModal } from './TemplateDeleteConfirmationModal'

export interface TemplateDetailsBoxProps {
  templateValue: TemplateValue
  readOnly: boolean
  showControls?: boolean
  origin?: TemplateOrigin
}

export const TemplateDetailsBox: React.FC<TemplateDetailsBoxProps> = ({ templateValue, readOnly, showControls = true, origin }) => {
  const navigate = useNavigate()
  const { accountId, account } = useAccount()
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateTemplateMutation()
  const [cloneTemplate, { isLoading: isCloning }] = useCloneTemplateMutation()
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteTemplateMutation()
  const [createNode] = useCreateNodeMutation()
  const [updateNode] = useUpdateNodeMutation()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showUsedInModal, setShowUsedInModal] = useState(false)
  const [showChangeAccessModal, setShowChangeAccessModal] = useState(false)
  const [showDownloadToast, setShowDownloadToast] = useState<boolean>(false)
  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const isSchedulingEmail = isSchedulingEmailTemplate(templateValue)
  const adminManaged = isAdminManagedTemplate(templateValue)
  const canChangeAccess = !adminManaged && hasTemplateAdminScope && ADMIN_EDITABLE_TEMPLATE_SCOPES.has(templateValue.scope)

  const { data: templateReferencesData } = useGetTemplateReferencesQuery({ accountId, templateId: templateValue.id }, { refetchOnMountOrArgChange: true })
  const referenceItems = templateReferencesData?.items ?? []
  const referenceCount = referenceItems.length

  const templatesLocked = account?.enterpriseConfig.lockTemplates ?? false
  const canDelete =
    !templatesLocked &&
    (adminManaged
      ? hasTemplateAdminScope && templateValue.scope === 'account'
      : templateValue.scope === 'user' || (hasTemplateAdminScope && ADMIN_EDITABLE_TEMPLATE_SCOPES.has(templateValue.scope)))
  const showDetailsPencil = isSchedulingEmail && !readOnly && templateValue.scope === 'account'
  const showExportAndUsedIn = !isSchedulingEmail

  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const onDuplicateClick = () => {
    setShowDuplicateModal(true)
  }

  const _handleEdit = async (request: TemplateUpdateRequest) => {
    await updateTemplate({ accountId, templateId: templateValue.id, request }).unwrap()
    setShowEditModal(false)
  }
  const handleEdit = tracked(
    _handleEdit,
    { eventName: TrackEvent.TEMPLATE_SAVED },
    { eventDetailsExtractor: () => ({ ...getTemplateLibraryTrackingDetails(templateValue), field: 'metadata' }) },
  )

  const handleCancelEdit = () => {
    setShowEditModal(false)
  }

  const handleDuplicate = async (request: TemplateCloneRequest) => {
    const clone = await cloneTemplate({ accountId, templateId: templateValue.id, request }).unwrap()
    if (origin) {
      const isUserScope = request.scope === 'user'
      let nodeContext: InputParametersHierarchyContext
      if (isUserScope) {
        nodeContext = { ...origin.context, userId: currentUserId }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { userId: _userId, ...contextWithoutUserId } = origin.context
        nodeContext = contextWithoutUserId
      }
      const originIsAtUserLevel = Boolean(origin.context.userId)
      if (origin.existingNodeId && originIsAtUserLevel === isUserScope) {
        await updateNode({
          accountId,
          type: origin.type,
          typeId: origin.typeId,
          nodeId: origin.existingNodeId,
          node: { templateId: clone.id },
        }).unwrap()
      } else {
        await createNode({
          accountId,
          type: origin.type,
          typeId: origin.typeId,
          node: { hierarchyId: calculateHierarchyId(origin.type, nodeContext), templateId: clone.id },
        }).unwrap()
      }
    }
    setShowDuplicateModal(false)
    navigate(`/workspace/setup/templates/template/${clone.id}`, { replace: true, state: origin ? { origin } : undefined })
  }

  const handleCancelDuplication = () => {
    setShowDuplicateModal(false)
  }

  const _handleChangeAccess = async (targetScope: CustomTemplateScope, replaceWithTemplateId?: string) => {
    await updateTemplate({
      accountId,
      templateId: templateValue.id,
      request: { scope: targetScope, ...(replaceWithTemplateId ? { replaceWithTemplateId } : {}) },
    }).unwrap()
    dispatch(inputParametersHierarchyApi.util.invalidateTags(['InputParametersHierarchy']))
    setShowChangeAccessModal(false)
  }
  const handleChangeAccess = tracked(
    _handleChangeAccess,
    { eventName: TrackEvent.TEMPLATE_ACCESS_CHANGED },
    {
      eventDetailsExtractor: (targetScope, replaceWithTemplateId) => ({
        ...getTemplateLibraryTrackingDetails(templateValue),
        fromScope: templateValue.scope,
        toScope: targetScope,
        hasReplacement: replaceWithTemplateId != null,
      }),
    },
  )

  const onDeleteClick = () => {
    setShowDeleteModal(true)
  }

  const _handleDelete = async (replaceWithTemplateId?: string | null) => {
    await deleteTemplate({ accountId, templateId: templateValue.id, replaceWithTemplateId }).unwrap()
    dispatch(inputParametersHierarchyApi.util.invalidateTags(['InputParametersHierarchy']))
    setShowDeleteModal(false)
    navigate(-1)
  }
  const handleDelete = tracked(
    _handleDelete,
    { eventName: TrackEvent.TEMPLATE_DELETED },
    {
      eventDetailsExtractor: (replaceWithTemplateId) => ({
        ...getTemplateLibraryTrackingDetails(templateValue),
        hasReplacement: replaceWithTemplateId != null,
      }),
    },
  )

  const handleCancelDeletion = () => {
    setShowDeleteModal(false)
  }

  const handleTemplateDetailsClicked = () => {
    if (readOnly) {
      return
    }
    setShowEditModal(true)
  }

  const typeDetails = useMemo(() => (isSchedulingEmail ? schedulingEmailDetails(templateValue) : []), [isSchedulingEmail, templateValue])

  const nameAndDescription = (
    <>
      <Row className='pb-3'>
        <Col className='d-flex justify-content-between align-items-start gap-2'>
          <h5>{templateValue.name}</h5>
          {showDetailsPencil ? (
            <OverlayTrigger placement='top' overlay={<Tooltip>Edit template details</Tooltip>}>
              <Button variant='grayGhost' className='button-large px-2' aria-label='Edit template details' onClick={handleTemplateDetailsClicked}>
                <i className='bi bi-pencil' />
              </Button>
            </OverlayTrigger>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col>
          <div className='paragraph-medium zocks-text-gray-600'>{templateValue.description}</div>
        </Col>
      </Row>
    </>
  )

  const handleExportTemplate = async () => {
    const formJson = JSON.stringify(
      {
        type: templateValue.type,
        typeId: templateValue.typeId,
        name: templateValue.name,
        description: templateValue.description,
        template: hasLexicalEmailFormat(templateValue.template) ? templateValue.template : templateValue.template.template,
      },
      null,
      2,
    )

    const blob = new Blob([formJson], { type: 'application/json' })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    const safeTemplateName = (() => {
      if (!templateValue.name) {
        return 'template'
      }
      const sanitized = templateValue.name
        .replace(/[^a-z0-9]/gi, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
      return sanitized || 'template'
    })()
    link.href = url
    link.download = `${safeTemplateName}_template.json`
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    setShowDownloadToast(true)
  }

  return (
    <>
      {showEditModal ? (
        <TemplateEditModal show={showEditModal} loading={isUpdating} templateValue={templateValue} onSave={handleEdit} onCancel={handleCancelEdit} />
      ) : null}
      {showDuplicateModal ? (
        <TemplateDuplicateModal
          show={showDuplicateModal}
          loading={isCloning}
          templateValue={templateValue}
          onDuplicate={handleDuplicate}
          onCancel={handleCancelDuplication}
          origin={origin}
        />
      ) : null}
      {showDeleteModal ? (
        <TemplateDeleteConfirmationModal
          show={showDeleteModal}
          loading={isDeleting}
          onCancel={handleCancelDeletion}
          onDelete={handleDelete}
          templateValue={templateValue}
        />
      ) : null}
      {showUsedInModal ? <TemplateUsedInModal show={showUsedInModal} items={referenceItems} onClose={() => setShowUsedInModal(false)} /> : null}
      {showChangeAccessModal ? (
        <TemplateChangeAccessModal
          show={showChangeAccessModal}
          loading={isUpdating}
          templateValue={templateValue}
          referenceCount={referenceCount}
          onConfirm={handleChangeAccess}
          onCancel={() => setShowChangeAccessModal(false)}
        />
      ) : null}
      <SuccessToast
        position={'bottom-center'}
        message={`Template "${templateValue.name}" downloaded.`}
        show={showDownloadToast}
        onClose={() => setShowDownloadToast(false)}
      />
      <Container className='zocks-bg-color-white p-3 border rounded-3'>
        {isSchedulingEmail ? (
          <div className='template-name-description-container'>{nameAndDescription}</div>
        ) : (
          <OverlayTrigger placement='top' overlay={<Tooltip>Edit template details</Tooltip>} show={readOnly ? false : undefined}>
            <div className={classNames('template-name-description-container', { enabled: !readOnly })} onClick={handleTemplateDetailsClicked}>
              {nameAndDescription}
            </div>
          </OverlayTrigger>
        )}
        <Row className='pt-4 pb-1'>
          <Col className='d-flex align-items-center'>
            <TemplateDetailsLabel label='Type' />
          </Col>
          <Col className='d-flex align-items-center justify-content-start'>
            <TemplateTypeBadge templateValue={templateValue} />
          </Col>
        </Row>
        {typeDetails.map(({ label, value }) => (
          <Row key={label} className='pt-3 pb-1'>
            <Col className='d-flex align-items-center'>
              <TemplateDetailsLabel label={label} />
            </Col>
            <Col className='d-flex align-items-center justify-content-start paragraph-small'>{value}</Col>
          </Row>
        ))}
        <Row className='pt-3 pb-1'>
          <Col className='d-flex align-items-center'>
            <TemplateDetailsLabel label='Access' />
          </Col>
          <Col className='d-flex align-items-center justify-content-start paragraph-small'>
            {canChangeAccess ? (
              <button
                type='button'
                className={`template-badge ${TemplateScopeMapping[templateValue.scope].color} zocks-text-gray-900 border-0 d-inline-flex align-items-center gap-1`}
                aria-expanded={showChangeAccessModal}
                onClick={() => setShowChangeAccessModal(true)}
              >
                <i className={`bi ${TemplateScopeMapping[templateValue.scope].icon} me-1`} />
                {TemplateScopeMapping[templateValue.scope].title}
                <i className='bi bi-pencil ms-1' />
              </button>
            ) : (
              <TemplateScopeBadge scope={templateValue.scope} />
            )}
          </Col>
        </Row>
        {showExportAndUsedIn ? (
          <Row className='pt-3 pb-1'>
            <Col className='d-flex align-items-center'>
              <TemplateDetailsLabel label='Used in' />
            </Col>
            <Col className='d-flex align-items-center justify-content-start paragraph-small'>
              {referenceCount > 0 ? (
                <button
                  type='button'
                  className='template-used-in-tag d-inline-flex align-items-center gap-1 px-2 py-1 rounded-2 border bg-white zocks-text-gray-900 zocks-border-color-gray-500 no-wrap'
                  aria-expanded={showUsedInModal}
                  onClick={() => setShowUsedInModal(true)}
                >
                  <span className='text-nowrap'>
                    {referenceCount} meeting type{referenceCount === 1 ? '' : 's'}
                  </span>
                  {showUsedInModal ? <i className='ps-3 pe-2 bi bi-pencil' /> : <i className='ps-3 pe-2 bi bi-pencil' />}
                </button>
              ) : (
                <span className='zocks-text-gray-700'>Not used</span>
              )}
            </Col>
          </Row>
        ) : null}
        <Row className='pt-3 pb-1'>
          <Col className='d-flex align-items-center'>
            <TemplateDetailsLabel label='Created by' />
          </Col>
          <Col className='d-flex justify-content-start paragraph-small'>
            {'createdBy' in templateValue && templateValue.createdBy !== ZOCKS_SYSTEM_USER_ID ? (
              <TemplateCreatedBy userId={templateValue.createdBy} />
            ) : templateValue.scope === 'system' || ('createdBy' in templateValue && templateValue.createdBy === ZOCKS_SYSTEM_USER_ID) ? (
              <SystemUserBadge />
            ) : null}
          </Col>
        </Row>
        {'updatedAt' in templateValue ? (
          <Row className='pt-3 pb-1'>
            <Col className='d-flex align-items-center'>
              <TemplateDetailsLabel label='Updated' />
            </Col>
            <Col className='d-flex align-items-center justify-content-start'>
              <div className='paragraph-small zocks-text-gray-900'>
                <FormattedDate date={templateValue.updatedAt} />
              </div>
            </Col>
          </Row>
        ) : null}
        {showControls ? (
          <Row className='pt-4'>
            <Col className='d-flex justify-content-start gap-2'>
              {!templatesLocked ? (
                <Button variant='grayGhost' className='button-large' onClick={onDuplicateClick} title='Duplicate'>
                  <i className='bi bi-copy' /> Duplicate
                </Button>
              ) : null}
              {showExportAndUsedIn ? (
                <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Export template to file</Tooltip>}>
                  <Button variant='grayGhost' className='button-large' onClick={handleExportTemplate} aria-label='Export template to file'>
                    <i className='bi bi-download' />
                  </Button>
                </OverlayTrigger>
              ) : null}
              {canDelete ? (
                <Button variant='grayGhost' className='button-large px-2' onClick={onDeleteClick} title='Delete' loading={isDeleting}>
                  <i className='bi bi-trash' />
                </Button>
              ) : null}
            </Col>
          </Row>
        ) : null}
      </Container>
    </>
  )
}

interface TemplateDetailsLabelProps {
  label: string
}

const TemplateDetailsLabel: React.FC<TemplateDetailsLabelProps> = ({ label }) => {
  return <div className='paragraph-small fw-bold zocks-text-gray-600'>{label}</div>
}

interface TemplateCreatedByProps {
  userId: string
}

const TemplateCreatedBy: React.FC<TemplateCreatedByProps> = ({ userId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: user } = useGetUserByIdQuery({ accountId, userId })
  if (!user) {
    return <div>{userId}</div>
  }

  return <TemplateCreatedByUserBadge createdByUser={user.data} />
}
